import { camelCaseToKebabCase, getFieldName, updateWidgetValue, useApp, useAppDispatch, useBehavior, useInputPhone, useTriggerAction } from '@libs/core';
import { THandleChangeInputPhone, TWidgetProps } from '@libs/types';
import React, { useEffect, useRef, useState } from 'react';
import { useConsumerInput } from '../../hooks/useInputs';
import { useGetTelegramPhoneNumber } from '../../hooks/useTelegramMiniApp';
import { TConsumerInputPhoneData, TConsumerItem, TInputPhoneMessages } from '../../types/data';
import { TInputOption } from '../../types/options';
import { InputPhoneView } from './InputPhoneView';
import { getFormattedMaxLength } from './utils/inputPhone';

/**
 * Phone input widget driven by server-provided configuration. Owns all behavior:
 * input/behavior/trigger hooks, country-selection state, prefix normalization, and
 * the change/select handlers. The overlay rendering (country modal + error popup)
 * lives in InputPhoneView; this widget only supplies the callbacks and state it needs.
 */
const InputPhoneWidget = ({ widget }: TWidgetProps<TConsumerInputPhoneData, TInputOption>) => {
    const phoneRef = useRef<HTMLInputElement | null>(null);
    const data = widget.data ?? {};
    const options = widget.options ?? {};
    const messages = (data?.messages ?? {}) as TInputPhoneMessages;
    const { isDisabled, isHidden } = useBehavior(widget);
    const { triggerActionsByType } = useTriggerAction(widget);
    const { setModal, setPopup } = useApp();
    const { value, isFocused, isError, errorMessage, label, helperText, handleFocus, handleBlur } = useConsumerInput(widget, {
        isHidden,
        isDisabled,
        triggerActionsByType,
        setModal,
    });
    const { iso2, handleChangeInputPhone } = useInputPhone(widget);
    const [itemId, setItemId] = useState(data.items?.find((i) => i.countryIso === iso2)?.id ?? widget?.data?.value);
    const hasItems = Array.isArray(data.items) && data.items.length > 0;
    const dispatch = useAppDispatch();

    const selectedItem = data.items?.find((i) => i.id === itemId) ?? data.items?.find((i) => i.countryIso === iso2);
    const prefixDigits = String((selectedItem?.value as string) ?? '').replace(/\D/g, '');
    // Both limits count digits (country code included, no `+`). The item wins when both are present.
    const maxLengthDigits = selectedItem?.maxLength ?? options.maxLength;
    const maxLength = maxLengthDigits ? getFormattedMaxLength(maxLengthDigits, iso2, prefixDigits) : undefined;

    const handleChangePhone: THandleChangeInputPhone = (value, countryData, event, formattedValue) => {
        const digits = formattedValue?.replace(/\D/g, '') ?? '';
        // The limit counts digits, so the extra ones are cut off rather than the whole change dropped:
        // react-phone-input-2 re-reads `value` only when the prop changes, so a dropped change would leave the
        // field showing a number the store does not hold. The native maxLength alone is not enough — a paste is
        // capped in characters and then re-formatted by the library, which can add digits back.
        const clearFormattedValue = maxLengthDigits ? digits.slice(0, maxLengthDigits) : digits;
        const nextFormattedValue = clearFormattedValue === digits ? formattedValue : clearFormattedValue;
        const prefix = ((countryData?.dialCode as string) ?? '').replace(/\D/g, '') || prefixDigits;
        if (prefix && clearFormattedValue === prefix) {
            dispatch(updateWidgetValue({ value: '', uid: widget.uid }));
        } else {
            handleChangeInputPhone(value, countryData, event, nextFormattedValue);
        }
    };

    const didInitPrefixCheck = useRef(false);
    useEffect(() => {
        if (didInitPrefixCheck.current) {
            return;
        }
        didInitPrefixCheck.current = true;
        const clearValue = String(value ?? '').replace(/\D/g, '');
        if (prefixDigits && clearValue === prefixDigits) {
            dispatch(updateWidgetValue({ value: '', uid: widget.uid }));
        }
    }, [dispatch, prefixDigits, value, widget.uid]);

    const displayValue = value || prefixDigits;

    useGetTelegramPhoneNumber(widget, handleChangeInputPhone);

    const onSelectItem = (_wid: string, item: TConsumerItem) => {
        if (hasItems && item) {
            setModal(null);
            setItemId(item.id);
            handleChangeInputPhone?.(
                item.metaText ?? '',
                {
                    countryCode: item.icon ?? '',
                    regex: item.regex,
                    isChangeCountry: true,
                },
                undefined,
                item.metaText,
            );
            //@ts-expect-error
            // https://github.com/bl00mber/react-phone-input-2/issues/187#issuecomment-799988994
            phoneRef?.current?.numberInputRef?.focus?.();
        }
    };

    return (
        <InputPhoneView
            widget={widget}
            uid={widget.uid}
            label={label}
            helperText={helperText}
            isError={isError}
            errorMessage={errorMessage}
            isFocused={isFocused}
            isDisabled={isDisabled}
            isHidden={isHidden}
            margin={options.margin}
            iso2={iso2}
            hasItems={hasItems}
            items={data.items}
            displayValue={displayValue}
            value={value}
            selectedItemId={itemId as string | undefined}
            selectPrefixHeaderText={messages.headerForSelectPhonePrefix}
            errorDescriptionTemplate={messages.errorMessageDescription}
            retryButtonLabel={messages.buttonFirstLabelLabel}
            retryButtonIcon={data.buttonFirstLabelIcon}
            editButtonLabel={messages.buttonSecondLabelLabel}
            editButtonIcon={data.buttonSecondLabelIcon}
            phoneRef={phoneRef}
            autoFocus={options.onFocus || false}
            name={getFieldName(options.autofill, 'tel')}
            autoComplete={camelCaseToKebabCase(options.autofill ?? 'tel')}
            required={data.mandatory}
            maxLength={maxLength}
            tabIndex={data.tabIndex}
            setModal={setModal}
            setPopup={setPopup}
            onSelectItem={onSelectItem}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={handleChangePhone}
        />
    );
};

export { InputPhoneWidget };
